import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { SHIFT_DISCOVERY_LIST_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useGetWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShifts";
import { addMinutes, isAfter, isBefore, isEqual, parseISO } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { DELAYED_REVIEW_STORAGE_KEY, INITIAL_TRIGGER_DELAY_MS } from "./constants";
import { type DelayedReviewStoredData } from "./types";

interface UseDelayedReviewProps {
  isEnabled: boolean;
  delayMinutes: number;
  workerTimezone: string | undefined;
}

interface UseDelayedReviewReturn {
  activeReview: DelayedReviewStoredData | null;
  modalState: ReturnType<typeof useModalState>;
  onComplete: () => void;
}

function hasDelayElapsed(shiftEndTimeIso: string, delayMinutes: number): boolean {
  const shiftEndTime = parseISO(shiftEndTimeIso);
  const eligibleAt = addMinutes(shiftEndTime, delayMinutes);
  const now = new Date();

  return isAfter(now, eligibleAt) || isEqual(now, eligibleAt);
}

export function useDelayedReview(props: UseDelayedReviewProps): UseDelayedReviewReturn {
  const { isEnabled, delayMinutes, workerTimezone } = props;

  const location = useLocation();

  const [pendingReview, , clearPendingReview] = useLocalStorage<DelayedReviewStoredData | null>(
    DELAYED_REVIEW_STORAGE_KEY,
    null
  );

  const [activeReview, setActiveReview] = useState<DelayedReviewStoredData | null>(null);
  const modalState = useModalState();
  const triggeredRef = useRef(false);

  const {
    shifts,
    isLoading: areShiftsLoading,
    isErrored: areShiftsErrored,
  } = useGetWorkerShifts({
    worker: { tmz: workerTimezone ?? "" },
  });

  /**
   * Consider worker to be in an "active" shift when they have not clocked out and either:
   * - The shift has started (start <= now), or
   * - A clock-in time exists (clockInOut.start is defined)
   */
  const isWorkerInActiveShift = useMemo(() => {
    if (areShiftsLoading || areShiftsErrored) {
      return true; // Assume true while loading/errored to avoid showing modal prematurely
    }

    const now = new Date();

    return shifts.some((shift) => {
      if (shift.clockInOut?.end) {
        return false;
      }

      const hasShiftStarted =
        isBefore(parseISO(shift.start), now) || isEqual(parseISO(shift.start), now);
      const hasClockedIn = shift.clockInOut?.start !== undefined;

      return hasShiftStarted || hasClockedIn;
    });
  }, [areShiftsLoading, areShiftsErrored, shifts]);

  const isDelayElapsed =
    isDefined(pendingReview) && isDefined(pendingReview?.shiftEndTime)
      ? hasDelayElapsed(pendingReview.shiftEndTime, delayMinutes)
      : false;

  const pathnameWithoutTrailingSlash = location.pathname.replace(/\/$/, "");
  const isOnShiftDiscoveryPage = pathnameWithoutTrailingSlash.endsWith(SHIFT_DISCOVERY_LIST_PATH);

  const shouldTrigger = isDelayElapsed && !isWorkerInActiveShift && isOnShiftDiscoveryPage;

  function onComplete() {
    modalState.closeModal();
    setActiveReview(null);
  }

  useEffect(() => {
    if (!isEnabled || !shouldTrigger || triggeredRef.current) {
      return;
    }

    // Small delay to ensure the app/page is fully loaded before showing modal
    const timer = setTimeout(() => {
      if (triggeredRef.current) {
        return;
      }
      triggeredRef.current = true;
      setActiveReview(pendingReview);
      clearPendingReview();
      logEvent(APP_V2_USER_EVENTS.DELAYED_WORKPLACE_REVIEW_MODAL_OPENED, {
        shiftId: pendingReview?.shiftId,
        facilityId: pendingReview?.facilityId,
      });
      modalState.openModal();
    }, INITIAL_TRIGGER_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, shouldTrigger]);

  return {
    activeReview,
    modalState,
    onComplete,
  };
}
